import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { inr, fmtDate, fmtDateTime, COMPANY_STYLES, API } from "@/api";
import { useCompany } from "@/store";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Send, Star } from "lucide-react";

const J_STYLE = {
  Assigned: "bg-primary-tint text-primary border-primary/30",
  "In Progress": "bg-warning text-warning-foreground border-warning",
  Completed: "bg-success-tint text-success border-success/30",
  Closed: "bg-muted text-muted-foreground border-border",
};
const CP_LABELS = { start_duty: "Start Duty", arrived: "Arrived at Site", job_completed: "Job Completed", end_duty: "End Duty" };

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [staff, setStaff] = useState([]);
  const [tab, setTab] = useState("All");
  const [assignFor, setAssignFor] = useState(null);
  const [assign, setAssign] = useState({ team_leader_id: "", scheduled_date: "", start_time: "", duration_minutes: 120 });
  const [detail, setDetail] = useState(null);
  const { company: companyCtx } = useCompany();

  const load = useCallback(() => {
    api.get("/jobs", { params: companyCtx !== "all" ? { company: companyCtx } : {} }).then((r) => setJobs(r.data));
    api.get("/users/field-staff").then((r) => setStaff(r.data));
  }, [companyCtx]);
  useEffect(() => { load(); }, [load]);

  const openDetail = (j) => api.get(`/jobs/${j.id}`).then((r) => setDetail(r.data));

  const saveAssign = async (e) => {
    e.preventDefault();
    try {
      const patch = { team_leader_id: assign.team_leader_id, scheduled_date: assign.scheduled_date };
      if (assign.start_time) {
        patch.scheduled_start_time = assign.start_time;
        patch.duration_minutes = parseInt(assign.duration_minutes) || 120;
        patch.booking_status = "Confirmed";
      }
      await api.patch(`/jobs/${assignFor.id}`, patch);
      toast.success("Job assigned & team notified");
      setAssignFor(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const dispatch = async (j) => {
    try {
      await api.post(`/jobs/${j.id}/dispatch`);
      toast.success("Customer notified: team on the way");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const filtered = tab === "All" ? jobs : jobs.filter((j) => j.status === tab);

  return (
    <div className="space-y-5" data-testid="jobs-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Jobs</h1>
        <p className="text-sm text-muted-foreground">Assign teams, track field execution, verify checkpoints</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["All", "Assigned", "In Progress", "Completed", "Closed"].map((s) => (
          <button key={s} data-testid={`job-tab-${s.toLowerCase().replace(/ /g, "-")}`} onClick={() => setTab(s)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tab === s ? "bg-primary text-inverse border-primary" : "bg-card hover:bg-accent"}`}>
            {s} {s !== "All" && <span className="opacity-70">({jobs.filter((j) => j.status === s).length})</span>}
          </button>
        ))}
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="jobs-table">
          <thead className="bg-muted/60">
            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th className="px-4 py-3">Job No</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Brand</th>
              <th className="px-4 py-3">Team Leader</th><th className="px-4 py-3">Scheduled</th><th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((j) => (
              <tr key={j.id} data-testid={`job-row-${j.id}`} onClick={() => openDetail(j)} className="border-t hover:bg-accent/40 cursor-pointer transition-colors">
                <td className="px-4 py-3 font-mono text-xs">{j.job_no}</td>
                <td className="px-4 py-3"><div className="font-medium">{j.customer_name}</div><div className="text-xs text-muted-foreground">{j.service}</div></td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full border ${COMPANY_STYLES[j.company] || ""}`}>{j.company}</span></td>
                <td className="px-4 py-3">{j.team_leader_name || <span className="text-danger-foreground text-xs font-semibold">Unassigned</span>}</td>
                <td className="px-4 py-3 text-xs">{fmtDate(j.scheduled_date)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full border ${J_STYLE[j.status] || ""}`}>{j.status}</span>
                  {j.rating && <span className="ml-1.5 text-xs text-warning-foreground inline-flex items-center gap-0.5"><Star size={11} fill="currentColor" />{j.rating}</span>}
                </td>
                <td className="px-4 py-3 text-right font-mono">{inr(j.amount)}</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1.5 justify-end">
                    {!j.team_leader_id && <Button size="sm" data-testid={`job-assign-${j.id}`} onClick={() => { setAssignFor(j); setAssign({ team_leader_id: "", scheduled_date: j.scheduled_date || "", start_time: j.scheduled_start_time || "", duration_minutes: j.duration_minutes || 120 }); }} className="h-8 bg-primary hover:bg-primary-hover">Assign</Button>}
                    {j.team_leader_id && j.status === "Assigned" && <Button size="sm" variant="outline" data-testid={`job-dispatch-${j.id}`} onClick={() => dispatch(j)} className="h-8"><Send size={13} className="mr-1" /> Dispatch</Button>}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">No jobs here — approve a quotation to create one</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!assignFor} onOpenChange={() => setAssignFor(null)}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader><DialogTitle className="font-heading">Assign {assignFor?.job_no}</DialogTitle></DialogHeader>
          <form onSubmit={saveAssign} className="space-y-3">
            <Select value={assign.team_leader_id} onValueChange={(v) => setAssign({ ...assign, team_leader_id: v })}>
              <SelectTrigger data-testid="assign-leader-select"><SelectValue placeholder="Select team leader" /></SelectTrigger>
              <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            <div>
              <label className="text-sm font-medium">Scheduled date</label>
              <Input data-testid="assign-date-input" type="date" value={assign.scheduled_date} onChange={(e) => setAssign({ ...assign, scheduled_date: e.target.value })} required className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Start time</label>
                <Input data-testid="assign-time-input" type="time" value={assign.start_time} onChange={(e) => setAssign({ ...assign, start_time: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <label className="text-sm font-medium">Duration (min)</label>
                <Input data-testid="assign-duration-input" type="number" min="30" step="30" value={assign.duration_minutes} onChange={(e) => setAssign({ ...assign, duration_minutes: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <div className="text-xs text-muted-foreground">For conflict-checked booking with AI slot suggestions, use the <b>Calendar</b> page.</div>
            <Button data-testid="assign-save-button" type="submit" disabled={!assign.team_leader_id} className="w-full bg-primary hover:bg-primary-hover">Assign & Notify</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Sheet open={!!detail} onOpenChange={() => setDetail(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="job-detail-sheet">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-heading font-mono">{detail.job_no}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-5 text-sm">
                <div className="flex gap-2 flex-wrap">
                  <span className={`text-xs px-2 py-1 rounded-full border ${COMPANY_STYLES[detail.company] || ""}`}>{detail.company}</span>
                  <span className={`text-xs px-2 py-1 rounded-full border ${J_STYLE[detail.status] || ""}`}>{detail.status}</span>
                  {detail.otp_verified && <span className="text-xs px-2 py-1 rounded-full border bg-success-tint text-success border-success/30">OTP Verified</span>}
                </div>
                <div className="space-y-1.5">
                  <div><span className="text-muted-foreground">Customer:</span> {detail.customer_name} · {detail.phone}</div>
                  <div><span className="text-muted-foreground">Service:</span> {detail.service} — <b className="font-mono">{inr(detail.amount)}</b></div>
                  <div><span className="text-muted-foreground">Address:</span> {detail.address || "—"}</div>
                  <div><span className="text-muted-foreground">Team:</span> {detail.team_leader_name || "Unassigned"} · Scheduled {fmtDate(detail.scheduled_date)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Checkpoints ({detail.checkpoints.length})</div>
                  <div className="space-y-2">
                    {detail.checkpoints.map((c) => (
                      <div key={c.id} className="border rounded-md p-3 flex gap-3 items-center">
                        {c.photo_url && <img src={`${API.replace("/api", "")}/api${c.photo_url.replace("/api", "")}`} alt="" className="w-14 h-14 rounded object-cover border" onError={(e) => { e.target.style.display = "none"; }} />}
                        <div className="flex-1">
                          <div className="font-medium">{CP_LABELS[c.type] || c.type}</div>
                          <div className="text-xs text-muted-foreground">{c.employee_name} · {fmtDateTime(c.created_at)}</div>
                          {c.distance_m != null && <div className={`text-xs font-semibold ${c.geofence_pass ? "text-success" : "text-danger"}`}>{c.geofence_pass ? "✓" : "✗"} Geofence {c.distance_m}m</div>}
                        </div>
                      </div>
                    ))}
                    {detail.checkpoints.length === 0 && <div className="text-muted-foreground">No field activity yet</div>}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Expenses ({detail.expenses.length})</div>
                  {detail.expenses.map((e) => (
                    <div key={e.id} className="border rounded-md p-3 mb-2 flex justify-between">
                      <span>{e.category} — {e.note || "—"} <span className="text-xs text-muted-foreground">by {e.employee_name}</span></span>
                      <b className="font-mono">{inr(e.amount)}</b>
                    </div>
                  ))}
                </div>
                {detail.bill && (
                  <div className="border rounded-md p-3 bg-muted/40">
                    <div className="font-mono text-xs">{detail.bill.invoice_no}</div>
                    <div className="mt-1">Billed <b className="font-mono">{inr(detail.bill.total_with_gst)}</b> (incl. GST) · <span className={`${detail.bill.payment_status === "Full" ? "text-success" : "text-danger"} font-semibold`}>{detail.bill.payment_status}</span></div>
                  </div>
                )}
                {detail.rating && <div className="border rounded-md p-3">Customer rating: <span className="text-warning-foreground font-bold">{detail.rating}/5</span> {detail.rating_comment && `— “${detail.rating_comment}”`}</div>}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
